import React, { useEffect, useState } from 'react';
import { Briefcase, Send, Smile, Award, Users, TrendingUp, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const API_URL = 'http://localhost:8081/api/offers';
const CANDIDATURE_URL = 'http://localhost:8081/api/candidatures';

interface Offer {
  id?: number;
  title: string;
  type: 'Stage' | 'Emploi';
  description: string;
  location: string;
  date: string;
}

const advantages = [
  { icon: <Smile className="text-green-500 w-6 h-6" />, text: "Équipe passionnée et dynamique" },
  { icon: <TrendingUp className="text-blue-600 w-6 h-6" />, text: "Projets innovants en cybersécurité" },
  { icon: <Award className="text-red-500 w-6 h-6" />, text: "Opportunités de formation et d'évolution" },
  { icon: <Users className="text-blue-600 w-6 h-6" />, text: "Environnement de travail flexible" },
  { icon: <Smile className="text-green-500 w-6 h-6" />, text: "Culture d'entreprise inclusive" },
];

const Careers = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [error, setError] = useState('');

  // État pour le formulaire de candidature spontanée
  const [candidature, setCandidature] = useState({
    nom: '',
    email: '',
    message: '',
    cv: null as File | null,
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetch(API_URL)
      .then(res => {
        if (!res.ok) throw new Error('Erreur lors du chargement des offres');
        return res.json();
      })
      .then(setOffers)
      .catch(() => setError("Erreur lors du chargement des offres"));
  }, []);

  // Gestion des champs du formulaire
  const handleCandidatureChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type, files } = e.target as HTMLInputElement;
    if (type === 'file' && files) {
      setCandidature(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setCandidature(prev => ({ ...prev, [name]: value }));
    }
  };

  // Soumission du formulaire
  const handleCandidatureSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    const formData = new FormData();

    // ✅ attention : 'name' (et non 'nom') doit correspondre au @RequestParam("name") du backend
    formData.append('name', candidature.nom);
    formData.append('email', candidature.email);
    formData.append('message', candidature.message);
    if (candidature.cv) formData.append('cv', candidature.cv);

    try {
      const response = await fetch("http://localhost:8081/api/candidature/spontanee", {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error("Erreur lors de l'envoi de la candidature");

      setCandidature({ nom: '', email: '', message: '', cv: null });

      Swal.fire({
        icon: 'success',
        title: 'Candidature envoyée !',
        text: 'Votre candidature a bien été envoyée. Nous vous contacterons si un poste correspond à votre profil.',
        confirmButtonColor: '#22c55e',
      });
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.message || 'Erreur lors de l\'envoi',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen py-16 px-2 md:px-0 flex flex-col items-center bg-gradient-to-br from-red-100 via-white to-green-100">
        {/* Offres d'emploi/stage */}
        <div className="max-w-3xl w-full mx-auto mb-12">
          <div className="flex flex-col items-center mb-4">
            <Briefcase className="w-10 h-10 text-green-600 mb-2 animate-bounce" />
            <h3 className="text-3xl font-extrabold mb-2 text-center text-green-700 tracking-tight drop-shadow">Offres d'emploi / stage</h3>
          </div>
          <div className="bg-white/90 p-8 rounded-3xl shadow-2xl border-2 border-green-200 text-center">
            {error && <p className="text-red-500">{error}</p>}
            {offers.length === 0 && !error && (
              <p className="text-gray-600 text-lg flex items-center justify-center gap-2">
                <FileText className="w-5 h-5 text-red-400" /> Aucun poste n'est ouvert actuellement.
              </p>
            )}
            {offers.length > 0 && (
              <ul className="space-y-6">
                {offers.map((offer) => (
                  <li key={offer.id} className="bg-white/80 rounded-xl shadow p-6 text-left border-2 border-green-100">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                      <span className="font-bold text-green-700 text-lg">{offer.title}</span>
                      <span className="text-sm px-2 py-1 rounded bg-green-100 text-green-700 inline-block">{offer.type}</span>
                    </div>
                    <div className="text-gray-700 mb-1">{offer.description}</div>
                    <div className="text-sm text-gray-500">{offer.location} — {offer.date}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Formulaire de candidature spontanée */}
        <div className="max-w-3xl w-full mx-auto mb-12">
          <div className="flex flex-col items-center mb-4">
            <Send className="w-10 h-10 text-red-400 mb-2 animate-pulse" />
            <h3 className="text-3xl font-extrabold mb-2 text-center text-red-500 tracking-tight drop-shadow">Candidature spontanée</h3>
          </div>
          <form className="bg-white/90 p-10 rounded-3xl shadow-2xl border-2 border-red-200 space-y-6" onSubmit={handleCandidatureSubmit} encType="multipart/form-data">
            <div className="relative">
              <label className="block text-red-700 mb-2 font-semibold">Nom</label>
              <input type="text" name="nom" value={candidature.nom} onChange={handleCandidatureChange} className="w-full border-2 border-red-200 rounded-xl px-5 py-3 bg-white/80 focus:border-red-400 focus:ring-2 focus:ring-red-100 transition shadow-sm" placeholder="Votre nom" required />
            </div>
            <div className="relative">
              <label className="block text-red-700 mb-2 font-semibold">Email</label>
              <input type="email" name="email" value={candidature.email} onChange={handleCandidatureChange} className="w-full border-2 border-red-200 rounded-xl px-5 py-3 bg-white/80 focus:border-red-400 focus:ring-2 focus:ring-red-100 transition shadow-sm" placeholder="Votre email" required />
            </div>
            <div className="relative">
              <label className="block text-red-700 mb-2 font-semibold">CV</label>
              <input type="file" name="cv" onChange={handleCandidatureChange} className="w-full border-2 border-red-200 rounded-xl px-5 py-3 bg-white/80 focus:border-red-400 focus:ring-2 focus:ring-red-100 transition file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100" />
            </div>
            <div className="relative">
              <label className="block text-red-700 mb-2 font-semibold">Message</label>
              <textarea name="message" value={candidature.message} onChange={handleCandidatureChange} className="w-full border-2 border-red-200 rounded-xl px-5 py-3 bg-white/80 focus:border-red-400 focus:ring-2 focus:ring-red-100 transition shadow-sm" rows={4} placeholder="Votre message" required />
            </div>
            <button type="submit" disabled={sending} className="w-full bg-gradient-to-r from-green-400 via-green-500 to-red-400 text-white font-bold py-3 rounded-xl shadow-md hover:scale-105 hover:shadow-lg transition-all duration-200 text-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
              <Send className="w-5 h-5" /> {sending ? 'Envoi en cours...' : 'Envoyer'}
            </button>
          </form>
        </div>

        {/* Avantages */}
        <div className="max-w-3xl w-full mx-auto">
          <div className="flex flex-col items-center mb-4">
            <Smile className="w-10 h-10 text-green-500 mb-2 animate-spin-slow" />
            <h3 className="text-2xl font-bold mb-2 text-center text-green-500">Avantages à rejoindre GoSecuNet</h3>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gradient-to-br from-green-50 via-blue-50 to-red-50 p-8 rounded-2xl shadow-lg border border-red-100">
            {advantages.map((adv, idx) => (
              <li key={idx} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/80 transition group">
                <span className="group-hover:scale-125 transition-transform duration-200">{adv.icon}</span>
                <span className="text-gray-800 font-medium group-hover:text-blue-700">{adv.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Careers;